/**
 * Animazioni GSAP delle pagine "La Scuola di Armando".
 *
 * È il markup a dichiarare cosa animare, con l'attributo `data-anim`; qui c'è
 * solo il dizionario dei tipi e la regia. Gli elementi sono nascosti da CSS
 * (`.js [data-anim]`) finché questo modulo non li prende in carico, così non
 * si vedono comparire prima dell'animazione.
 *
 * Tipi disponibili:
 *  - `lines` titoli display: le righe salgono da dietro una maschera
 *  - `text`  corpo testo: le righe salgono in dissolvenza, senza maschera
 *  - `words` parole in dissolvenza con sfocatura (lead e pull quote)
 *  - `fade`  blocco intero verso l'alto
 *  - `soft`  sola opacità, per gli elementi che hanno già un transform da CSS
 *  - `pop`   opacità + scala
 *  - `media` immagini: tendina dal basso via clip-path
 *  - `rows`  figli in sequenza, tendina da sinistra (tabella delle mostre)
 *
 * Attributi opzionali: `data-anim-delay`, `data-anim-stagger` (secondi).
 *
 * Quanto è sopra la piega parte al caricamento, il resto quando entra in
 * viewport. Con `prefers-reduced-motion` non si anima nulla.
 */
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { SplitText } from 'gsap/SplitText';

gsap.registerPlugin(ScrollTrigger, SplitText);

const EASE = 'expo.out';
const START = 'top 88%';

function num(el, attr, fallback) {
	const value = parseFloat(el.getAttribute(attr));

	return isNaN(value) ? fallback : value;
}

/**
 * Gli wysiwyg di ACF annidano il testo in <p>: si divide paragrafo per
 * paragrafo, così SplitText non deve clonare i blocchi per trovare le righe.
 */
function splitTargets(el) {
	const paragraphs = el.querySelectorAll('p, li');

	return paragraphs.length ? Array.from(paragraphs) : [el];
}

/**
 * Sopra la piega si parte subito (regia affidata a `data-anim-delay`), sotto
 * si aspetta lo scroll. Lo si decide una volta sola, all'avvio: SplitText può
 * rifare la divisione più tardi e la posizione nel frattempo cambia.
 */
function scrollVars(el) {
	if (el.getBoundingClientRect().top < window.innerHeight * 0.95) {
		return null;
	}

	return { trigger: el, start: START, once: true };
}

function withScroll(vars, scroll) {
	if (scroll) {
		vars.scrollTrigger = scroll;
	}

	return vars;
}

const builders = {
	lines(el, delay, stagger, scroll) {
		SplitText.create(splitTargets(el), {
			type: 'lines',
			mask: 'lines',
			linesClass: 'at-line',
			autoSplit: true,
			onSplit(self) {
				gsap.set(el, { autoAlpha: 1 });

				return gsap.fromTo(
					self.lines,
					{ yPercent: 115 },
					withScroll({
						yPercent: 0,
						duration: 1.2,
						ease: EASE,
						delay: delay,
						stagger: stagger || 0.09,
					}, scroll)
				);
			},
		});
	},

	text(el, delay, stagger, scroll) {
		SplitText.create(splitTargets(el), {
			type: 'lines',
			linesClass: 'at-textline',
			autoSplit: true,
			onSplit(self) {
				gsap.set(el, { autoAlpha: 1 });

				return gsap.fromTo(
					self.lines,
					{ y: 26, autoAlpha: 0 },
					withScroll({
						y: 0,
						autoAlpha: 1,
						duration: 0.9,
						ease: 'power3.out',
						delay: delay,
						stagger: stagger || 0.045,
					}, scroll)
				);
			},
		});
	},

	words(el, delay, stagger, scroll) {
		SplitText.create(splitTargets(el), {
			type: 'words',
			wordsClass: 'at-word',
			onSplit(self) {
				gsap.set(el, { autoAlpha: 1 });

				return gsap.fromTo(
					self.words,
					{ yPercent: 30, autoAlpha: 0, filter: 'blur(10px)' },
					withScroll({
						yPercent: 0,
						autoAlpha: 1,
						filter: 'blur(0px)',
						duration: 1,
						ease: 'power3.out',
						delay: delay,
						stagger: stagger || 0.028,
					}, scroll)
				);
			},
		});
	},

	fade(el, delay, stagger, scroll) {
		gsap.fromTo(
			el,
			{ y: 36, autoAlpha: 0 },
			withScroll({
				y: 0,
				autoAlpha: 1,
				duration: 1,
				ease: 'power3.out',
				delay: delay,
			}, scroll)
		);
	},

	soft(el, delay, stagger, scroll) {
		gsap.fromTo(
			el,
			{ autoAlpha: 0 },
			withScroll({
				autoAlpha: 1,
				duration: 1.1,
				ease: 'power2.out',
				delay: delay,
			}, scroll)
		);
	},

	pop(el, delay, stagger, scroll) {
		gsap.fromTo(
			el,
			{ autoAlpha: 0, scale: 0.7, transformOrigin: 'left top' },
			withScroll({
				autoAlpha: 1,
				scale: 1,
				duration: 1.1,
				ease: EASE,
				delay: delay,
			}, scroll)
		);
	},

	media(el, delay, stagger, scroll) {
		gsap.fromTo(
			el,
			{ clipPath: 'inset(0% 0% 100% 0%)', autoAlpha: 1 },
			withScroll({
				clipPath: 'inset(0% 0% 0% 0%)',
				duration: 1.4,
				ease: 'power4.inOut',
				delay: delay,
			}, scroll)
		);
	},

	rows(el, delay, stagger, scroll) {
		gsap.set(el, { autoAlpha: 1 });

		gsap.fromTo(
			Array.from(el.children),
			{ clipPath: 'inset(0% 100% 0% 0%)' },
			withScroll({
				clipPath: 'inset(0% 0% 0% 0%)',
				duration: 0.9,
				ease: 'power3.out',
				delay: delay,
				stagger: stagger || 0.055,
			}, scroll)
		);
	},
};

export default function initSchoolAnimations() {
	// Dice allo script di anim-boot.php che le animazioni sono in mano a
	// qualcuno: senza questa riga lui scopre gli elementi da solo.
	document.documentElement.setAttribute('data-anim-ready', '');

	const items = gsap.utils.toArray('[data-anim]');

	if (!items.length) {
		return;
	}

	// Rete di sicurezza: qualunque cosa vada storta, il testo resta leggibile.
	const revealAll = () => gsap.set(items, { autoAlpha: 1 });

	if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
		revealAll();
		return;
	}

	try {
		items.forEach((el) => {
			const build = builders[el.getAttribute('data-anim')];

			if (!build) {
				gsap.set(el, { autoAlpha: 1 });
				return;
			}

			build(
				el,
				num(el, 'data-anim-delay', 0),
				num(el, 'data-anim-stagger', 0),
				scrollVars(el)
			);
		});
	} catch (error) {
		console.error('schoolAnimations', error);
		revealAll();
		return;
	}

	// Le maschere di SplitText cambiano l'altezza delle righe e i font arrivano
	// dopo il primo layout: i trigger vanno ricalcolati a caratteri caricati.
	if (document.fonts && document.fonts.ready) {
		document.fonts.ready.then(() => ScrollTrigger.refresh());
	} else {
		ScrollTrigger.refresh();
	}
}
